import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, X, Lightbulb } from "lucide-react";
import { GUIDEBOOK } from "@/data/guidebook";
import { cn } from "@/lib/utils";

export function PageHeader({ title, subtitle, pageKey, actions, testid }) {
  const [open, setOpen] = useState(false);
  const cards = GUIDEBOOK[pageKey] || [];

  return (
    <div className="mb-6" data-testid={testid || "page-header"}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-[#F5F5F5]">{title}</h1>
            {cards.length > 0 && (
              <button
                onClick={() => setOpen(true)}
                data-testid="guidebook-open-btn"
                className="relative group flex items-center justify-center w-9 h-9 rounded-sm border border-[#333333] text-[#A3A3A3] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-colors"
                title="Open Guidebook"
              >
                <BookOpen className="w-4 h-4" strokeWidth={1.75} />
                <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 flex items-center justify-center text-[9px] font-bold rounded-full bg-[#D4AF37] text-[#0A0A0A]">
                  {cards.length}
                </span>
              </button>
            )}
          </div>
          {subtitle && <p className="text-sm text-[#A3A3A3] mt-1.5 max-w-2xl">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
              onClick={() => setOpen(false)}
              data-testid="guidebook-overlay"
            />
            <motion.aside
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
              className="fixed top-0 right-0 z-50 h-full w-full max-w-md bg-[#0D0D0D] border-l border-[#262626] flex flex-col"
              data-testid="guidebook-panel"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626]">
                <div className="flex items-center gap-2.5">
                  <Lightbulb className="w-4 h-4 text-[#D4AF37]" />
                  <div>
                    <div className="font-heading font-semibold text-[#F5F5F5]">Guidebook</div>
                    <div className="text-[11px] text-[#737373]">{title}</div>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} data-testid="guidebook-close-btn" className="text-[#737373] hover:text-[#F5F5F5] transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-5 space-y-3">
                {cards.map((c, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="rounded-md border border-[#262626] bg-[#141414] p-4 hover:border-[#333333] transition-colors"
                    data-testid={`guidebook-card-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-sm bg-[rgba(212,175,55,0.1)] text-[#D4AF37] text-xs font-bold flex items-center justify-center font-mono">
                        {i + 1}
                      </span>
                      <div>
                        <h4 className="font-heading font-semibold text-sm text-[#F5F5F5]">{c.title}</h4>
                        <p className="text-[13px] leading-relaxed text-[#A3A3A3] mt-1">{c.body}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
